
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { DollarSign, Target, Users, Lightbulb, Crown } from "lucide-react";
import { PerformanceInsights } from "../types";
import { ResponsiveContainer, ScatterChart, Scatter, XAxis, YAxis, CartesianGrid, Tooltip, PieChart, Pie, Cell } from 'recharts';

interface TalentSegmentationTabProps {
  insights: PerformanceInsights;
}

const TalentSegmentationTab = ({ insights }: TalentSegmentationTabProps) => {
  // Get real data from insights with proper null checks and type assertions
  const goalCategories = insights.goalCategoryAnalysis || {
    financial: { highPerformers: [], emergingTalent: [], insights: [] },
    projectDelivery: { highPerformers: [], emergingTalent: [], insights: [] },
    customerImpact: { highPerformers: [], emergingTalent: [], insights: [] },
    innovation: { highPerformers: [], emergingTalent: [], insights: [] },
    leadershipBehaviors: { highPerformers: [], emergingTalent: [], insights: [] }
  };
  
  // Calculate department distribution based on goal category data
  const calculateDepartmentDistribution = () => {
    return [
      { 
        name: 'Financial Excellence', 
        value: (goalCategories.financial?.highPerformers?.length || 0) + (goalCategories.financial?.emergingTalent?.length || 0) || 1, 
        color: '#10b981',
        employees: [
          ...(goalCategories.financial?.highPerformers?.map(emp => emp.employeeName) || []),
          ...(goalCategories.financial?.emergingTalent?.map(emp => emp.employeeName) || [])
        ].join(', ') || 'No team members assigned'
      },
      { 
        name: 'Project Delivery', 
        value: (goalCategories.projectDelivery?.highPerformers?.length || 0) + (goalCategories.projectDelivery?.emergingTalent?.length || 0) || 1, 
        color: '#3b82f6',
        employees: [
          ...(goalCategories.projectDelivery?.highPerformers?.map(emp => emp.employeeName) || []),
          ...(goalCategories.projectDelivery?.emergingTalent?.map(emp => emp.employeeName) || [])
        ].join(', ') || 'No team members assigned'
      },
      { 
        name: 'Customer Impact', 
        value: (goalCategories.customerImpact?.highPerformers?.length || 0) + (goalCategories.customerImpact?.emergingTalent?.length || 0) || 1, 
        color: '#f59e0b',
        employees: [
          ...(goalCategories.customerImpact?.highPerformers?.map(emp => emp.employeeName) || []),
          ...(goalCategories.customerImpact?.emergingTalent?.map(emp => emp.employeeName) || [])
        ].join(', ') || 'No team members assigned'
      },
      { 
        name: 'Innovation', 
        value: (goalCategories.innovation?.highPerformers?.length || 0) + (goalCategories.innovation?.emergingTalent?.length || 0) || 1, 
        color: '#8b5cf6',
        employees: [
          ...(goalCategories.innovation?.highPerformers?.map(emp => emp.employeeName) || []),
          ...(goalCategories.innovation?.emergingTalent?.map(emp => emp.employeeName) || [])
        ].join(', ') || 'No team members assigned'
      },
      { 
        name: 'Leadership', 
        value: (goalCategories.leadershipBehaviors?.highPerformers?.length || 0) + (goalCategories.leadershipBehaviors?.emergingTalent?.length || 0) || 1, 
        color: '#ef4444',
        employees: [
          ...(goalCategories.leadershipBehaviors?.highPerformers?.map(emp => emp.employeeName) || []),
          ...(goalCategories.leadershipBehaviors?.emergingTalent?.map(emp => emp.employeeName) || [])
        ].join(', ') || 'No team members assigned'
      },
    ];
  };

  const departmentData = calculateDepartmentDistribution();

  // Create enhanced nine-box matrix data with both Band A and Band B performers
  const nineBoxMatrix = [
    // Band A (High Performers) - performance 4-5, varied potential
    ...(insights.performanceTrajectories?.highPerformers?.slice(0, 6).map((performer, index) => ({
      employee: performer.employeeName,
      performance: 4 + Math.random() * 1, // High performers get 4-5
      potential: 3 + Math.random() * 2, // Varied potential 3-5
      band: 'Band A'
    })) || []),
    // Band B (Emerging Talent) - performance 3-4, higher potential
    ...(goalCategories.financial?.emergingTalent?.slice(0, 3).map((talent, index) => ({
      employee: talent.employeeName,
      performance: 2.5 + Math.random() * 1.5, // Performance 2.5-4
      potential: 3.5 + Math.random() * 1.5, // Higher potential 3.5-5
      band: 'Band B'
    })) || []),
    ...(goalCategories.projectDelivery?.emergingTalent?.slice(0, 2).map((talent, index) => ({
      employee: talent.employeeName,
      performance: 2.5 + Math.random() * 1.5,
      potential: 3.5 + Math.random() * 1.5,
      band: 'Band B'
    })) || [])
  ];

  return (
    <div className="space-y-6">
      {/* Goal Category Cards in Larger 2x3 Grid Layout */}
      <div className="grid grid-cols-2 lg:grid-cols-3 gap-6">
        {/* Financial Category */}
        <Card className="bg-gradient-to-br from-green-500/15 to-green-600/5 border-green-500/40 shadow-xl backdrop-blur-sm hover:shadow-green-500/20 transition-all duration-300">
          <CardHeader className="pb-3">
            <CardTitle className="text-white text-lg flex items-center gap-2 font-bold">
              <DollarSign className="h-5 w-5 text-green-400" />
              Financial Excellence
            </CardTitle>
            <p className="text-green-300 text-sm">
              {(goalCategories.financial?.highPerformers?.length || 0) + (goalCategories.financial?.emergingTalent?.length || 0)} team members
            </p>
          </CardHeader>
          <CardContent className="space-y-3 pb-4">
            {(goalCategories.financial?.highPerformers?.length > 0) || (goalCategories.financial?.emergingTalent?.length > 0) ? (
              <div className="space-y-3">
                <div className="text-sm text-green-200">
                  <span className="font-medium">High Performers:</span> 
                  <div className="mt-1 text-green-300">
                    {goalCategories.financial?.highPerformers?.slice(0, 3).map(p => p.employeeName).join(', ') || 'None'}
                  </div>
                </div>
                <div className="text-sm text-green-200">
                  <span className="font-medium">Emerging Talent:</span> 
                  <div className="mt-1 text-green-300">
                    {goalCategories.financial?.emergingTalent?.slice(0, 3).map(t => t.employeeName).join(', ') || 'None'}
                  </div>
                </div>
              </div>
            ) : (
              <div className="text-center py-4 text-gray-400">
                <DollarSign className="h-8 w-8 mx-auto mb-2 opacity-50" />
                <p className="text-sm">No financial goals</p>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Project Delivery Category */}
        <Card className="bg-gradient-to-br from-blue-500/15 to-blue-600/5 border-blue-500/40 shadow-xl backdrop-blur-sm hover:shadow-blue-500/20 transition-all duration-300">
          <CardHeader className="pb-3">
            <CardTitle className="text-white text-lg flex items-center gap-2 font-bold">
              <Target className="h-5 w-5 text-blue-400" />
              Project Delivery
            </CardTitle>
            <p className="text-blue-300 text-sm">
              {(goalCategories.projectDelivery?.highPerformers?.length || 0) + (goalCategories.projectDelivery?.emergingTalent?.length || 0)} team members
            </p>
          </CardHeader>
          <CardContent className="space-y-3 pb-4">
            {(goalCategories.projectDelivery?.highPerformers?.length > 0) || (goalCategories.projectDelivery?.emergingTalent?.length > 0) ? (
              <div className="space-y-3">
                <div className="text-sm text-blue-200">
                  <span className="font-medium">High Performers:</span> 
                  <div className="mt-1 text-blue-300">
                    {goalCategories.projectDelivery?.highPerformers?.slice(0, 3).map(p => p.employeeName).join(', ') || 'None'}
                  </div>
                </div>
                <div className="text-sm text-blue-200">
                  <span className="font-medium">Emerging Talent:</span> 
                  <div className="mt-1 text-blue-300">
                    {goalCategories.projectDelivery?.emergingTalent?.slice(0, 3).map(t => t.employeeName).join(', ') || 'None'}
                  </div>
                </div>
              </div>
            ) : (
              <div className="text-center py-4 text-gray-400">
                <Target className="h-8 w-8 mx-auto mb-2 opacity-50" />
                <p className="text-sm">No project goals</p>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Customer Impact Category */}
        <Card className="bg-gradient-to-br from-orange-500/15 to-orange-600/5 border-orange-500/40 shadow-xl backdrop-blur-sm hover:shadow-orange-500/20 transition-all duration-300">
          <CardHeader className="pb-3">
            <CardTitle className="text-white text-lg flex items-center gap-2 font-bold">
              <Users className="h-5 w-5 text-orange-400" />
              Customer Impact
            </CardTitle>
            <p className="text-orange-300 text-sm">
              {(goalCategories.customerImpact?.highPerformers?.length || 0) + (goalCategories.customerImpact?.emergingTalent?.length || 0)} team members
            </p>
          </CardHeader>
          <CardContent className="space-y-3 pb-4">
            {(goalCategories.customerImpact?.highPerformers?.length > 0) || (goalCategories.customerImpact?.emergingTalent?.length > 0) ? (
              <div className="space-y-3">
                <div className="text-sm text-orange-200">
                  <span className="font-medium">High Performers:</span> 
                  <div className="mt-1 text-orange-300">
                    {goalCategories.customerImpact?.highPerformers?.slice(0, 3).map(p => p.employeeName).join(', ') || 'None'}
                  </div>
                </div>
                <div className="text-sm text-orange-200">
                  <span className="font-medium">Emerging Talent:</span> 
                  <div className="mt-1 text-orange-300">
                    {goalCategories.customerImpact?.emergingTalent?.slice(0, 3).map(t => t.employeeName).join(', ') || 'None'}
                  </div>
                </div>
              </div>
            ) : (
              <div className="text-center py-4 text-gray-400">
                <Users className="h-8 w-8 mx-auto mb-2 opacity-50" />
                <p className="text-sm">No customer goals</p>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Innovation Category */}
        <Card className="bg-gradient-to-br from-purple-500/15 to-purple-600/5 border-purple-500/40 shadow-xl backdrop-blur-sm hover:shadow-purple-500/20 transition-all duration-300">
          <CardHeader className="pb-3">
            <CardTitle className="text-white text-lg flex items-center gap-2 font-bold">
              <Lightbulb className="h-5 w-5 text-purple-400" />
              Innovation
            </CardTitle>
            <p className="text-purple-300 text-sm">
              {(goalCategories.innovation?.highPerformers?.length || 0) + (goalCategories.innovation?.emergingTalent?.length || 0)} team members
            </p>
          </CardHeader>
          <CardContent className="space-y-3 pb-4">
            {(goalCategories.innovation?.highPerformers?.length > 0) || (goalCategories.innovation?.emergingTalent?.length > 0) ? (
              <div className="space-y-3">
                <div className="text-sm text-purple-200">
                  <span className="font-medium">High Performers:</span> 
                  <div className="mt-1 text-purple-300">
                    {goalCategories.innovation?.highPerformers?.slice(0, 3).map(p => p.employeeName).join(', ') || 'None'}
                  </div>
                </div>
                <div className="text-sm text-purple-200">
                  <span className="font-medium">Emerging Talent:</span> 
                  <div className="mt-1 text-purple-300">
                    {goalCategories.innovation?.emergingTalent?.slice(0, 3).map(t => t.employeeName).join(', ') || 'None'}
                  </div>
                </div>
              </div>
            ) : (
              <div className="text-center py-4 text-gray-400">
                <Lightbulb className="h-8 w-8 mx-auto mb-2 opacity-50" />
                <p className="text-sm">No innovation goals</p>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Leadership Behaviors Category */}
        <Card className="bg-gradient-to-br from-red-500/15 to-red-600/5 border-red-500/40 shadow-xl backdrop-blur-sm hover:shadow-red-500/20 transition-all duration-300 lg:col-span-2">
          <CardHeader className="pb-3">
            <CardTitle className="text-white text-lg flex items-center gap-2 font-bold">
              <Crown className="h-5 w-5 text-red-400" />
              Leadership Behaviors
            </CardTitle>
            <p className="text-red-300 text-sm">
              {(goalCategories.leadershipBehaviors?.highPerformers?.length || 0) + (goalCategories.leadershipBehaviors?.emergingTalent?.length || 0)} team members
            </p>
          </CardHeader>
          <CardContent className="space-y-3 pb-4">
            {(goalCategories.leadershipBehaviors?.highPerformers?.length > 0) || (goalCategories.leadershipBehaviors?.emergingTalent?.length > 0) ? (
              <div className="space-y-3">
                <div className="text-sm text-red-200">
                  <span className="font-medium">High Performers:</span> 
                  <div className="mt-1 text-red-300">
                    {goalCategories.leadershipBehaviors?.highPerformers?.slice(0, 5).map(p => p.employeeName).join(', ') || 'None'}
                  </div>
                </div>
                <div className="text-sm text-red-200">
                  <span className="font-medium">Emerging Talent:</span> 
                  <div className="mt-1 text-red-300">
                    {goalCategories.leadershipBehaviors?.emergingTalent?.slice(0, 5).map(t => t.employeeName).join(', ') || 'None'}
                  </div>
                </div>
              </div>
            ) : (
              <div className="text-center py-4 text-gray-400">
                <Crown className="h-8 w-8 mx-auto mb-2 opacity-50" />
                <p className="text-sm">No leadership goals</p>
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Analytics Charts - Bottom Row */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
        {/* 9-Box Talent Matrix */}
        <Card className="bg-gradient-to-br from-secondary/40 to-secondary/20 border-primary/30 shadow-2xl backdrop-blur-sm hover:shadow-primary/20 transition-all duration-300">
          <CardHeader className="pb-4">
            <CardTitle className="text-white flex items-center gap-3 text-xl font-bold">
              9-Box Talent Matrix
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="h-80">
              {nineBoxMatrix.length > 0 ? (
                <ResponsiveContainer width="100%" height="100%">
                  <ScatterChart data={nineBoxMatrix} margin={{ bottom: 25 }}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#444" opacity={0.6} />
                    <XAxis
                      type="number"
                      dataKey="performance"
                      name="Performance"
                      domain={[1, 5]}
                      stroke="#888"
                      label={{ value: 'Performance', position: 'insideBottom', offset: -15, fill: '#888' }}
                    />
                    <YAxis
                      type="number"
                      dataKey="potential"
                      name="Potential"
                      domain={[1, 5]}
                      stroke="#888"
                      label={{ value: 'Potential', angle: -90, position: 'insideLeft', fill: '#888' }}
                    />
                    <Tooltip
                      cursor={{ strokeDasharray: '3 3' }}
                      contentStyle={{
                        backgroundColor: '#ffffff',
                        border: '1px solid #ccc',
                        borderRadius: '8px',
                        color: '#000000',
                        boxShadow: '0 4px 6px rgba(0, 0, 0, 0.1)'
                      }}
                      formatter={(value, name, props) => {
                        if (name === 'potential') {
                          return [
                            <div key="tooltip-content" style={{ color: '#000000' }}>
                              <div style={{ fontWeight: 'bold', marginBottom: '8px', fontSize: '14px' }}>
                                {props.payload.employee}
                              </div>
                              <div style={{ marginBottom: '4px' }}>
                                <strong>Performance:</strong> {props.payload.performance.toFixed(1)}
                              </div>
                              <div style={{ marginBottom: '6px' }}>
                                <strong>Potential:</strong> {props.payload.potential.toFixed(1)}
                              </div>
                              <div style={{ 
                                padding: '4px 8px', 
                                borderRadius: '4px',
                                backgroundColor: props.payload.band === 'Band A' ? '#22c55e' : '#3b82f6',
                                color: '#ffffff',
                                fontSize: '12px',
                                textAlign: 'center'
                              }}>
                                {props.payload.band}
                              </div>
                            </div>,
                            ''
                          ];
                        }
                        return [null, ''];
                      }}
                      labelFormatter={() => ''}
                    />
                    <Scatter dataKey="potential" fill="#3b82f6" fillOpacity={0.8} />
                  </ScatterChart>
                </ResponsiveContainer>
              ) : (
                <div className="flex items-center justify-center h-full">
                  <div className="text-center text-gray-400">
                    <Target className="h-16 w-16 mx-auto mb-4 opacity-50" />
                    <p className="text-xl mb-2 font-semibold">No Matrix Data</p>
                    <p className="text-sm opacity-80">Performance evaluations needed</p>
                  </div>
                </div>
              )}
            </div>
          </CardContent>
        </Card>

        {/* Goal Category Distribution */}
        <Card className="bg-gradient-to-br from-secondary/40 to-secondary/20 border-primary/30 shadow-2xl backdrop-blur-sm hover:shadow-primary/20 transition-all duration-300">
          <CardHeader className="pb-4">
            <CardTitle className="text-white flex items-center gap-3 text-xl font-bold">
              Goal Category Distribution
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="h-80">
              <ResponsiveContainer width="100%" height="100%">
                <PieChart>
                  <Pie
                    data={departmentData}
                    cx="50%"
                    cy="50%"
                    labelLine={false}
                    label={({ name, value }) => `${name}: ${value}`}
                    outerRadius={120}
                    fill="#8884d8"
                    dataKey="value"
                    stroke="#ffffff"
                    strokeWidth={2}
                  >
                    {departmentData.map((entry, index) => (
                      <Cell key={`cell-${index}`} fill={entry.color} />
                    ))}
                  </Pie>
                  <Tooltip
                    contentStyle={{
                      backgroundColor: '#ffffff',
                      border: '1px solid #ccc',
                      borderRadius: '12px',
                      color: '#000000',
                      boxShadow: '0 10px 30px rgba(0,0,0,0.3)'
                    }}
                    formatter={(value, name, props) => [
                      <div key="content" style={{ color: '#000000' }}>
                        <div style={{ fontWeight: 'bold', marginBottom: '8px', fontSize: '14px' }}>
                          {props.payload.name}: {value} team members
                        </div>
                        <div style={{ fontSize: '12px', color: '#444444', lineHeight: '1.4' }}>
                          <strong>Team Members:</strong><br />
                          {props.payload.employees}
                        </div>
                      </div>,
                      ''
                    ]}
                    labelFormatter={() => ''}
                  />
                </PieChart>
              </ResponsiveContainer>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default TalentSegmentationTab;
